import { RiskRegister, TableExtendedRiskRegister } from "src/app/models/risk-assessment.model";
import {
    RiskRegisterAgentColumnResponse,
    RiskRegisterMatrixColumn,
    SegHeadcount,
    SegOrgLevelAssignment,
} from "src/app/models/risk-register-seg-page.model";

export interface ReplacedSegRows {
    rows: TableExtendedRiskRegister[];
    removedSegIds: string[];
}

export function preferOwnRegisters<T extends Pick<RiskRegister, "redirectedFromSegId">>(registers: T[]): T[] {
    return [...registers].sort(
        (left, right) => Number(left.redirectedFromSegId != null) - Number(right.redirectedFromSegId != null),
    );
}

export interface SegPageRowInfo {
    segHeadcounts?: Record<string, SegHeadcount>;
    segOrgLevels?: Record<string, SegOrgLevelAssignment[]>;
}

export function toAgentColumns(agents: RiskRegisterAgentColumnResponse[]): RiskRegisterMatrixColumn[] {
    return agents.map((agent) => ({
        agentId: agent.id,
        agentName: agent.name,
        headerName: agent.id,
        consequenceRating: agent.consequenceRating,
        registerCount: agent.registerCount,
    }));
}

export function orderAgentsLike<T extends { id: string }>(agents: T[], order: string[]): T[] {
    const rank = new Map(order.map((agentId, index) => [agentId, index]));
    return [...agents].sort((left, right) => (rank.get(left.id) ?? order.length) - (rank.get(right.id) ?? order.length));
}

export function visibleAgentColumns(
    columns: RiskRegisterMatrixColumn[],
    hiddenAgentIds: ReadonlySet<string>,
): RiskRegisterMatrixColumn[] {
    return columns.filter((column) => !hiddenAgentIds.has(column.agentId));
}

function orgLevelDisplay(assignments: SegOrgLevelAssignment[] | undefined): Pick<TableExtendedRiskRegister, "orgLevelName" | "orgLevelTitle"> {
    if (!assignments?.length) {
        return {};
    }
    return {
        orgLevelName: assignments.map((assignment) => assignment.organizationLevelName).join(", "),
        orgLevelTitle: assignments
            .map((assignment) => [...(assignment.parentPath ?? []), assignment.organizationLevelName].join(" > "))
            .join("\n"),
    };
}

export function buildSegRows(
    registers: RiskRegister[],
    columns: RiskRegisterMatrixColumn[],
    toCell: (register: RiskRegister) => RiskRegister,
    hiddenAgentIds: ReadonlySet<string> = new Set(),
    pageInfo: SegPageRowInfo = {},
): TableExtendedRiskRegister[] {
    const bySeg = new Map<string, RiskRegister[]>();
    registers.forEach((register) => {
        const group = bySeg.get(register.segId);
        if (group) {
            group.push(register);
        } else {
            bySeg.set(register.segId, [register]);
        }
    });
    return [...bySeg.values()].map((group) => {
        const own = preferOwnRegisters(group);
        const allRegisters = columns.map((column) => {
            const register = own.find((item) => item.agentId === column.agentId);
            return register ? toCell(register) : undefined;
        });
        const headcount = pageInfo.segHeadcounts?.[own[0].segId];
        return {
            ...own[0],
            ...(headcount ? { numberOfWorkers: headcount.numberOfWorkers, numberOfContractors: headcount.numberOfContractors } : {}),
            ...orgLevelDisplay(pageInfo.segOrgLevels?.[own[0].segId]),
            allRegisters,
            registers: projectCells(allRegisters, columns, hiddenAgentIds),
        } as TableExtendedRiskRegister;
    });
}

function projectCells(
    allCells: (RiskRegister | undefined)[],
    columns: RiskRegisterMatrixColumn[],
    hiddenAgentIds: ReadonlySet<string>,
): (RiskRegister | undefined)[] {
    return hiddenAgentIds.size ? allCells.filter((_, index) => !hiddenAgentIds.has(columns[index].agentId)) : allCells;
}

export function rebuildRowCells(
    rows: TableExtendedRiskRegister[],
    previousAgentIds: string[],
    columns: RiskRegisterMatrixColumn[],
    hiddenAgentIds: ReadonlySet<string>,
): TableExtendedRiskRegister[] {
    const previousIndex = new Map(previousAgentIds.map((agentId, index) => [agentId, index]));
    return rows.map((row) => {
        const previous = row.allRegisters ?? row.registers ?? [];
        const allRegisters = columns.map((column) => previous[previousIndex.get(column.agentId) ?? -1]);
        return { ...row, allRegisters, registers: projectCells(allRegisters, columns, hiddenAgentIds) };
    });
}

export function moveInSharedOrder(
    sharedOrder: string[],
    agentId: string,
    anchorAgentId: string,
    placeAfter: boolean,
): string[] {
    const without = sharedOrder.filter((id) => id !== agentId);
    const anchorIndex = without.indexOf(anchorAgentId);
    if (anchorIndex < 0 || agentId === anchorAgentId) {
        return sharedOrder;
    }
    without.splice(placeAfter ? anchorIndex + 1 : anchorIndex, 0, agentId);
    return without;
}

export function mergeSegRows(
    rows: TableExtendedRiskRegister[],
    incoming: TableExtendedRiskRegister[],
): TableExtendedRiskRegister[] {
    const known = new Set(rows.map((row) => row.segId));
    return [...rows, ...incoming.filter((row) => !known.has(row.segId))];
}

export function replaceSegRows(
    rows: TableExtendedRiskRegister[],
    refreshed: TableExtendedRiskRegister[],
    segIds: string[],
): ReplacedSegRows {
    const requested = new Set(segIds);
    const refreshedBySeg = new Map(refreshed.map((row) => [row.segId, row]));
    const removedSegIds = [...requested].filter((segId) => !refreshedBySeg.has(segId));
    const next = rows
        .map((row) => (requested.has(row.segId) ? refreshedBySeg.get(row.segId) : row))
        .filter((row): row is TableExtendedRiskRegister => row != null);
    return { rows: next, removedSegIds };
}
